import { Button, ButtonSize } from '@lemon8/design';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { useTranslation } from 'react-i18next';
import { useViewTracking } from '~/utils/use-view-tracking';
import { Article } from '@lemon8/webapp-seo-shared';
import { EndRecommendCard } from '~/components/end-recommend-card';
import { ModuleType } from '~/utils/module-type';
import { ClickPosition } from '~/utils/click-position';
import useReflowClick from '~/utils/use-reflow-click';
import { useActiveToFeedForyou } from '~/utils/active/use-active-to-feed';
import { Loading } from '~/components/loading';
import { useScopedAtomValue, useScopedAtom } from '@lemon8/webapp-shared/component';
import {
  $CurrentGalleryArticle,
  $MainSwiper,
  $EndRecommendSwiper,
  $CurrentArticleLoadableRelatedPost,
  $CurrentGallerySlide,
  $LoadArticleRelatedPosts,
  $CurrentMainSlide,
} from '~/components/article-gallery/atoms';

import './style.scss';

const EndRecommendContentLoading: FC = () => {
  return (
    <div className="gallery-end-recommend-loading">
      <Loading variant="ball" />
    </div>
  );
};

const EndRecommendContentEmpty: FC = () => {
  const { t } = useTranslation();
  return (
    <div className="gallery-end-recommend-empty">
      <span>{t('no_content')}</span>
    </div>
  );
};

const EndRecommendContent: FC = () => {
  const { groupId } = useScopedAtomValue($CurrentGalleryArticle);
  const mainSwiper = useScopedAtomValue($MainSwiper);
  const [endRecommendSwiper, setEndRecommendSwiper] = useScopedAtom($EndRecommendSwiper);
  const loadableRelatedPost = useScopedAtomValue($CurrentArticleLoadableRelatedPost);
  const { items, hasMore } = useMemo(() => {
    if (loadableRelatedPost.state === 'hasData') {
      return loadableRelatedPost.data;
    }

    return {
      items: [] as Article[],
      hasMore: true,
    };
  }, [loadableRelatedPost]);

  const endRecommendItems: Article[] = items.filter((item) => item.groupId !== groupId).slice(0, 5);

  useEffect(() => {
    if (!endRecommendSwiper) {
      return;
    }

    const handleProgress = (_s: SwiperClass, progress: number) => {
      if (!mainSwiper) {
        return;
      }

      if (progress) {
        mainSwiper.slideTo(1);
        mainSwiper.allowSlidePrev = false;
      } else {
        mainSwiper.allowSlidePrev = true;
      }
    };

    endRecommendSwiper.on('progress', handleProgress);

    return () => {
      endRecommendSwiper.off('progress', handleProgress);
    };
  }, [mainSwiper, endRecommendSwiper]);

  if (items.length === 0 && hasMore) {
    return <EndRecommendContentLoading />;
  }

  if (endRecommendItems.length <= 2) {
    return <EndRecommendContentEmpty />;
  }

  return (
    <Swiper resistanceRatio={0} slidesPerView={'auto'} onSwiper={setEndRecommendSwiper} nested>
      {endRecommendItems.map((item, rank) => (
        <SwiperSlide key={item.groupId}>
          <EndRecommendCard article={item} size="small" rank={rank} />
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export const EndRecommendSection: FC = () => {
  const { t } = useTranslation();
  const activeToFeed = useActiveToFeedForyou();
  const currentMainSlide = useScopedAtomValue($CurrentMainSlide);
  const currentGallerySlide = useScopedAtomValue($CurrentGallerySlide);
  const { imageList, groupId } = useScopedAtomValue($CurrentGalleryArticle);
  const { loader } = useScopedAtomValue($LoadArticleRelatedPosts);

  const seeMoreObservedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_module_show', {
        current_page_module: ModuleType.main_related_post,
        group_id: groupId,
      });
      window.Tea('wap_cta_show', {
        current_page_module: ModuleType.main_related_post,
        current_position: ClickPosition.main_see_more_btn,
        group_id: groupId,
      });
    }, []),
  });

  const handleEndRecommendBtnClick = useReflowClick(
    {
      clickEvent: () => ({
        current_position: ClickPosition.main_see_more_btn,
      }),
      reflowBehavior: () => {
        activeToFeed(ClickPosition.main_see_more_btn);
      },
      defaultBehavior: () => {
        activeToFeed(ClickPosition.main_see_more_btn);
      },
    },
    [activeToFeed],
  );

  useEffect(() => {
    if (!loader) {
      return;
    }

    if (imageList.length === 1) {
      if (currentMainSlide === 1) {
        loader();
      }
    } else {
      if (currentGallerySlide === imageList.length - 1) {
        loader();
      }
    }
  }, [currentMainSlide, currentGallerySlide, imageList, loader]);

  return (
    <SwiperSlide className="gallery-end-recommend-wrapper">
      <div className="gallery-end-recommend">
        <p className="title">{t('seo_v3_postDetail_recommended_title')}</p>
        <div className="main">
          <EndRecommendContent />
        </div>
        <div className="active-btn" ref={seeMoreObservedRef}>
          <Button
            variant="Secondary"
            size={ButtonSize.large}
            onClick={handleEndRecommendBtnClick}
            title={t('seo_v3_postDetail_openApp_btn')}
          />
        </div>
      </div>
    </SwiperSlide>
  );
};

EndRecommendSection.displayName = SwiperSlide.displayName;
